import { Area, AreaChart, ResponsiveContainer, Tooltip, XAxis } from "recharts";

export type TrendPoint = {
  /** Short x-axis label, e.g. "Sep". */
  label: string;
  value: number;
};

type TrendChartProps = {
  data: TrendPoint[];
  /** Distinguishes gradient ids when several charts share a page. */
  id: string;
  /** Series color, e.g. "hsl(var(--chart-1))". One color per category of project. */
  color: string;
  formatValue: (value: number) => string;
};

/**
 * The little product chart: one soft gradient area, an evenly spaced month
 * tick under every point (interval 0, so recharts never thins them into a
 * ragged subset), values only in the tooltip.
 */
export function TrendChart({ data, id, color, formatValue }: TrendChartProps) {
  const gradientId = `trend-fill-${id}`;

  return (
    <div className="h-28 w-full">
      <ResponsiveContainer width="100%" height="100%">
        {/* accessibilityLayer off: it makes the svg focusable, and clicking a
            decorative sparkline then paints a focus outline around the card. */}
        <AreaChart
          data={data}
          accessibilityLayer={false}
          className="outline-none [&_svg]:outline-none"
          margin={{ top: 4, right: 10, bottom: 0, left: 10 }}
        >
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity={0.35} />
              <stop offset="100%" stopColor={color} stopOpacity={0.02} />
            </linearGradient>
          </defs>
          <XAxis
            dataKey="label"
            tickLine={false}
            axisLine={false}
            interval={0}
            tickMargin={6}
            tick={{ fontSize: 9, fill: "var(--muted-foreground)" }}
          />
          <Tooltip
            cursor={{ stroke: "var(--border)" }}
            content={({ active, payload, label }) => {
              if (!active || !payload?.length) return null;
              return (
                <div className="rounded-md border border-border bg-popover px-2.5 py-1.5 text-xs text-popover-foreground shadow-sm">
                  <span className="text-muted-foreground">{label}</span>{" "}
                  <span className="font-medium">{formatValue(Number(payload[0].value))}</span>
                </div>
              );
            }}
          />
          <Area
            type="monotone"
            dataKey="value"
            stroke={color}
            strokeWidth={1.5}
            fill={`url(#${gradientId})`}
            isAnimationActive={false}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}
